import { defineStore, storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import type {
  RegistrationBillingInterval,
  RegistrationPlanTier
} from '@dashboard/modules/user/services/registrationPlans'
import {
  normalizeAgencyPlanOptions,
  type AgencyPlanApiResponse,
  type AgencyPlanOption
} from '@dashboard/modules/settings/services/agencyPricingOptions'
import api from '@dashboard/services/axiosApi'
import { useAuthStore } from './authStore'

export interface AgencyClient {
  id: number
  email: string
  name: string
  /** `trialing`, `lifetime`, `expired` (trial ended without a plan) or a Stripe subscription status. */
  status: string
  subscription_status?: string | null
  team_name?: string | null
  team_pid?: string | null
  trial_ends_at?: string | null
  created_at: string
}

export interface AgencyInvitation {
  id: number
  email: string
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  plan_tier?: RegistrationPlanTier | null
  billing_interval?: RegistrationBillingInterval | null
  expires_at: string
  accepted_at?: string | null
  created_at: string
  /** Null while the agency has no name, because the signup link is built from it. */
  signup_url: string | null
}

export interface AgencyPlansState {
  plans: AgencyPlanOption[]
  defaultTier: RegistrationPlanTier | null
  defaultInterval: RegistrationBillingInterval | null
}

export interface AgencyProfile {
  name: string | null
  logo: string | null
  welcome_message: string | null
  website: string | null
  tagline: string | null
  description: string | null
  location: string | null
  services_offered: string[]
  esps_proficient_in: string[]
  is_public: boolean
  /** Null until the agency has a name. */
  signup_url: string | null
}

export type AgencyProfileChanges = Partial<
  Pick<
    AgencyProfile,
    | 'name'
    | 'logo'
    | 'welcome_message'
    | 'website'
    | 'tagline'
    | 'description'
    | 'location'
    | 'services_offered'
    | 'esps_proficient_in'
    | 'is_public'
  >
>

export interface AffiliateStats {
  configured: boolean
  reward_tier: string | null
  commission_percentage: number | null
}

export interface AgencyOption {
  value: string
  label: string
}

export interface AgencyOptions {
  services: AgencyOption[]
  esps: AgencyOption[]
  countries: AgencyOption[]
}

export const useAgencyStore = defineStore('agency', () => {
  const { user } = storeToRefs(useAuthStore())

  const isAgency = computed(() => Boolean(user.value?.is_agency && user.value.agency))

  return { isAgency }
})

/*
 * Agency data loads only on the pages that show it. The store above is read on
 * every settings navigation (route guard and nav), so it must not fetch.
 */
function useAgencyQuery<T>(key: string, fetch: () => Promise<T>, options: { staleTime?: number } = {}) {
  const agencyStore = useAgencyStore()

  return useQuery({
    queryKey: ['agency', key],
    queryFn: fetch,
    enabled: () => agencyStore.isAgency,
    retry: false,
    ...options
  })
}

export function useAgencyProfile() {
  return useAgencyQuery('profile', () =>
    api.get<{ data: AgencyProfile }>('/api/agency/profile').then((res) => res.data.data)
  )
}

export function useAgencyOptions() {
  return useAgencyQuery(
    'options',
    () => api.get<{ data: AgencyOptions }>('/api/agency/options').then((res) => res.data.data),
    { staleTime: 1000 * 60 * 60 }
  )
}

export function useAgencyClients() {
  return useAgencyQuery('clients', () =>
    api.get<{ data: { clients: AgencyClient[] } }>('/api/agency/clients').then((res) => res.data.data.clients)
  )
}

export function useAgencyInvitations() {
  return useAgencyQuery('invitations', () =>
    api
      .get<{ data: { invitations: AgencyInvitation[] } }>('/api/agency/invitations')
      .then((res) => res.data.data.invitations)
  )
}

export function useAgencyPlans() {
  return useAgencyQuery('plans', () =>
    api
      .get<{
        data: {
          plans: AgencyPlanApiResponse[]
          default_tier: RegistrationPlanTier | null
          default_interval: RegistrationBillingInterval | null
        }
      }>('/api/agency/plans')
      .then(
        (res) =>
          ({
            plans: normalizeAgencyPlanOptions(res.data.data.plans),
            defaultTier: res.data.data.default_tier ?? null,
            defaultInterval: res.data.data.default_interval ?? null
          }) satisfies AgencyPlansState
      )
  )
}

/** Lowercased emails of clients credited to the agency's affiliate ID. */
export function useAgencyAffiliateReferrals() {
  return useAgencyQuery('affiliate-referrals', () =>
    api
      .get<{ data: { emails: string[] } }>('/api/agency/affiliate-referrals')
      .then((res) => res.data.data.emails.map((email) => email.toLowerCase()))
  )
}

export function useAgencyAffiliateStats() {
  return useAgencyQuery('affiliate-stats', () =>
    api.get<{ data: AffiliateStats }>('/api/agency/affiliate-stats').then((res) => res.data.data)
  )
}

/** Saves only the given profile fields. Callers report success or failure themselves. */
export function useUpdateAgencyProfile() {
  const queryClient = useQueryClient()

  return async (changes: AgencyProfileChanges) => {
    await api.put('/api/agency/profile', changes)
    void queryClient.invalidateQueries({ queryKey: ['agency', 'profile'] })
  }
}

/** Invitation actions; each refreshes the invitation list and leaves messages to the caller. */
export function useAgencyInvitationActions() {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['agency', 'invitations'] })

  return {
    async create(
      email: string,
      selection: { plan_tier?: RegistrationPlanTier; billing_interval?: RegistrationBillingInterval } = {}
    ) {
      await api.post('/api/agency/invitations', { email, ...selection })
      await refresh()
    },
    async cancel(id: number) {
      await api.delete(`/api/agency/invitations/${id}`)
      await refresh()
    },
    async resend(id: number) {
      await api.post(`/api/agency/invitations/${id}/resend`)
      await refresh()
    }
  }
}
